/**
 * Remote push registration (drawing reminders sent by the server scheduler).
 *
 *   iOS / Android  @capacitor/push-notifications → APNs token (iOS) / FCM token (Android)
 *   Web            Service worker + Push API with the server's VAPID key (public/push-sw.js)
 *   Dev web        no VAPID key → a mock device is registered; the server's mock sender logs reminders
 *
 * The device is (re-)registered with the reminder days / time, the device time zone, language and the
 * active profile whenever one of them changes. Local reminders (lib/platform.ts) stay as the fallback:
 * on native they are cancelled only once remote registration succeeded.
 */
import { PushNotifications } from '@capacitor/push-notifications';
import { api, onBeforeLogout } from './api';
import { cancelReminders, isNative, platform } from './platform';
import { APP_VERSION, useApp } from '../state/store';

let navigate: ((path: string) => void) | null = null;
/** App.tsx hands over the router so a tapped notification opens its link. */
export const setPushNavigator = (fn: (path: string) => void) => {
  navigate = fn;
};

let nativeToken: Promise<string> | null = null;
let lastKey = '';
let registeredToken: string | null = null;

function nativeRegistrationToken(): Promise<string> {
  nativeToken ??= new Promise<string>((resolve, reject) => {
    void PushNotifications.addListener('registration', (t) => resolve(t.value));
    void PushNotifications.addListener('registrationError', (e) => reject(new Error(e.error)));
    void PushNotifications.addListener('pushNotificationActionPerformed', (a) => {
      const link = (a.notification.data as { link?: string } | undefined)?.link;
      if (link && link.startsWith('/')) navigate?.(link);
    });
    void PushNotifications.register();
  }).catch((e) => {
    nativeToken = null;
    throw e;
  });
  return nativeToken;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function webSubscription(vapidKey: string): Promise<PushSubscriptionJSON | null> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  // Production: the PWA service worker imports push-sw.js. Dev (no PWA worker): register it directly.
  const reg = (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.register('/push-sw.js'));
  await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidKey) }));
  return sub.toJSON();
}

const mockWebToken = () => {
  const k = 'sketchling-push-mock-id';
  try {
    let id = localStorage.getItem(k);
    if (!id) localStorage.setItem(k, (id = `mock-web-${crypto.randomUUID()}`));
    return id;
  } catch {
    return `mock-web-${crypto.randomUUID()}`;
  }
};

/** Permission without prompting (the prompt is owned by the reminder UI). */
async function hasPermission(): Promise<boolean> {
  if (isNative) {
    try {
      return (await PushNotifications.checkPermissions()).receive === 'granted';
    } catch {
      return false;
    }
  }
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/**
 * Android remote push needs Firebase (google-services.json); without it PushNotifications.register() can crash the app.
 * scripts/ops/build-android.sh sets VITE_FCM_ENABLED=1 only when that file exists. Local reminders keep working.
 */
const NATIVE_PUSH_READY = platform !== 'android' || import.meta.env.VITE_FCM_ENABLED === '1';

/** Register / refresh this device with the server. Safe to call often (no-op when nothing changed). */
export async function syncPushRegistration(force = false): Promise<boolean> {
  const s = useApp.getState();
  if (!s.account.accessToken || s.config.enableRemotePush === false) return false;
  if (isNative && !NATIVE_PUSH_READY) return false;
  if (!(await hasPermission())) return false;
  const profile = s.profiles.find((p) => p.id === s.activeProfileId);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const r = s.settings.reminders;
  const base = {
    locale: s.settings.language,
    timeZone,
    profileId: profile?.remoteId ?? null,
    appVersion: APP_VERSION,
    reminders: { enabled: r.enabled, days: r.days, time: r.time },
  };
  const key = JSON.stringify([s.account.userId, base]);
  if (!force && key === lastKey) return true;
  try {
    if (isNative) {
      const token = await nativeRegistrationToken();
      await api.pushRegister({ ...base, provider: platform === 'ios' ? 'apns' : 'fcm', platform, token });
      registeredToken = token;
      // Remote reminders are live: drop the local copies to avoid double notifications.
      await cancelReminders();
    } else {
      const { vapidPublicKey } = await api.pushConfig();
      const sub = vapidPublicKey ? await webSubscription(vapidPublicKey) : null;
      if (sub) {
        await api.pushRegister({ ...base, provider: 'webpush', platform: 'web', subscription: sub });
        registeredToken = sub.endpoint ?? null;
      } else if (import.meta.env.DEV) {
        const token = mockWebToken();
        await api.pushRegister({ ...base, provider: 'webpush', platform: 'web', token });
        registeredToken = token;
      } else return false;
    }
    lastKey = key;
    return true;
  } catch {
    return false;
  }
}

/** Remove this device (sign-out). */
export async function unregisterPush(): Promise<void> {
  if (!registeredToken) return;
  await api.pushUnregister(registeredToken).catch(() => undefined);
  registeredToken = null;
  lastKey = '';
}

let started = false;
/** Watch the store and keep the registration current. */
export function initPush(): void {
  if (started) return;
  started = true;
  onBeforeLogout(unregisterPush);
  let timer: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void syncPushRegistration(), 1500);
  };
  useApp.subscribe((st, prev) => {
    if (
      st.settings.reminders !== prev.settings.reminders ||
      st.settings.language !== prev.settings.language ||
      st.account.accessToken !== prev.account.accessToken ||
      st.activeProfileId !== prev.activeProfileId ||
      st.config.enableRemotePush !== prev.config.enableRemotePush
    )
      schedule();
  });
  schedule();
}
